import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Tag,
  Button,
  Upload,
  Tooltip,
  Select,
  Empty,
  Alert,
  Popconfirm,
  Space,
  Progress,
  Typography,
} from 'antd';
import {
    CheckCircleFilled, ClockCircleOutlined, CloseCircleFilled,
    LinkOutlined, DeleteOutlined, StopOutlined, ReloadOutlined, PaperClipOutlined,
    FileDoneOutlined, CheckCircleOutlined, PlusOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';
import axiosInstance from '../variables/api/axiosInstance';
import { TYPES_DOC_GED_MAP, HAB, can } from '../variables/constants';
import { toast } from 'react-toastify';
import { avecLibelleTraduit } from '../i18n/libelles';
import { localeIntl } from '../i18n/format';
import CBModal from './CBModal';

const { Text } = Typography;

const STATUT_CONFIG = avecLibelleTraduit({
    ATTENDUE: { color: '#f97316', bg: '#fff7ed', icon: <ClockCircleOutlined /> },
    RECUE: { color: '#166534', bg: '#dcfce7', icon: <CheckCircleFilled /> },
    REJETEE: { color: '#991b1b', bg: '#fee2e2', icon: <CloseCircleFilled /> },
}, 'commun:checklistPieces.statuts');

/**
 * Composant checklist des pièces administratives d'un dossier de réclamation.
 *
 * Usage :
 *   <ChecklistPieces
 *     dossierTrackingId="..."
 *     sinistreTrackingId="..."   // pour charger les docs GED disponibles
 *     victimeTrackingId="..."
 *     onDocumentTeleverse={() => ...}   // facultatif : un fichier vient d'entrer dans la GED
 *   />
 */
export default function ChecklistPieces({ dossierTrackingId, sinistreTrackingId, victimeTrackingId, onDocumentTeleverse }) {
    const { t } = useTranslation('commun');
    const [maturite, setMaturite] = useState(null);
    const [loading, setLoading] = useState(false);
    const [docsGed, setDocsGed] = useState([]);
    const [modalPiece, setModalPiece] = useState(null); // pièce en cours d'association
    const [docSelectionne, setDocSelectionne] = useState(null);
    // Pièces dont le fichier part ou attend son tour : { [trackingId]: true }.
    const [envois, setEnvois] = useState({});
    const fileParTypeGed = useRef({});
    const fileAssociations = useRef(Promise.resolve());
    const envoisEnCours = useRef(0);
    const unEnvoiReussi = useRef(false);
    const dernierChargement = useRef(0);

    const load = useCallback(async () => {
        if (!dossierTrackingId) return;
        // Des envois simultanés rechargent la liste chacun leur tour : seule la
        // réponse la plus récente s'affiche, une plus ancienne arrivée après l'écraserait.
        const numero = ++dernierChargement.current;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/dossiers/${dossierTrackingId}/pieces`);
            if (numero === dernierChargement.current) setMaturite(res.data?.data);
        } catch { if (numero === dernierChargement.current) toast.error(t('checklistPieces.erreurChargement')); }
        finally { if (numero === dernierChargement.current) setLoading(false); }
    }, [dossierTrackingId, t]);

    const loadDocsGed = useCallback(async () => {
        try {
            // Docs GED : priorité aux docs de la victime, fallback sur ceux du sinistre entier
            if (victimeTrackingId) {
                const res = await axiosInstance
                    .get(`/v1/ged/victimes/${victimeTrackingId}/documents`)
                    .catch(() => ({ data: { data: [] } }));
                setDocsGed(res.data?.data || []);
                return;
            }
            if (sinistreTrackingId) {
                const res = await axiosInstance
                    .get(`/v1/ged/sinistres/${sinistreTrackingId}/documents`)
                    .catch(() => ({ data: { data: [] } }));
                setDocsGed(res.data?.data || []);
            }
        } catch { /* silencieux */ }
    }, [sinistreTrackingId, victimeTrackingId]);

    useEffect(() => { load(); }, [load]);

    const ouvrirAssociation = async (piece) => {
        await loadDocsGed();
        setModalPiece(piece);
        setDocSelectionne(null);
    };

    const associer = async () => {
        if (!docSelectionne) return toast.warn(t('checklistPieces.selectionnezDocument'));
        try {
            await axiosInstance.post(`/v1/dossiers/pieces/${modalPiece.trackingId}/associer`, {
                ossanGedDocumentTrackingId: docSelectionne,
            });
            toast.success(t('checklistPieces.documentAssocie'));
            setModalPiece(null);
            load();
        } catch { toast.error(t('checklistPieces.erreurAssociation')); }
    };

    /*
     * « + » : téléverser le fichier d'une pièce directement depuis la liste.
     * « Associer » suppose le document déjà dans la GED ; il fallait l'y déposer
     * ailleurs puis revenir ici. Le fichier part dans la GED de la victime, sous le
     * type GED de la pièce, puis il est associé à CETTE ligne. L'association
     * explicite n'est pas un doublon de l'auto-association du serveur : celle-ci
     * prend la première pièce ATTENDUE du même type, ignore les pièces REJETEE, et
     * ne fait rien pour une pièce sans type GED (« Autre document »).
     *
     * Plusieurs pièces s'envoient en même temps, avec deux files d'attente :
     *   - une par type GED : le serveur associe tout dépôt à la PREMIÈRE pièce
     *     attendue de son type. Deux dépôts simultanés du même type (« Factures
     *     médicales » corporelle et matérielle d'un dossier mixte) visaient donc la
     *     même ligne, et le second pouvait écraser l'association du premier ;
     *   - une seule pour les associations : chacune recalcule la maturité du
     *     sinistre et ne voit que les pièces déjà enregistrées. En parallèle, les deux
     *     dernières pièces ne se voyaient pas et le dossier ne passait jamais MÛR.
     * Les transferts de types différents, la partie longue, restent simultanés.
     */
    const televerser = (piece, file) => {
        const estPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        if (!estPdf) {
            toast.error(t('checklistPieces.formatNonAccepte'));
            return;
        }
        const typeDocument = piece.typeDocumentGed || 'AUTRES';
        setEnvois((e) => ({ ...e, [piece.trackingId]: true }));
        envoisEnCours.current += 1;

        const envoyer = async () => {
            try {
                const metadata = {
                    titre: `${piece.typePieceLibelle} - ${file.name.replace(/\.pdf$/i, '')}`,
                    typeDocument,
                    dateDocument: dayjs().format('YYYY-MM-DD'),
                    sinistreTrackingId,
                    victimeTrackingId,
                    dossierReclamationTrackingId: dossierTrackingId,
                };
                const formData = new FormData();
                formData.append('file', file);
                formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
                const res = await axiosInstance.post('/v1/ged/documents', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                const docId = res.data?.data?.ossanGedDocumentTrackingId;
                if (!docId) throw new Error('Document GED non retourné');
                const association = fileAssociations.current.then(() =>
                    axiosInstance.post(`/v1/dossiers/pieces/${piece.trackingId}/associer`, {
                        ossanGedDocumentTrackingId: docId,
                    }));
                fileAssociations.current = association.catch(() => {});
                await association;
                unEnvoiReussi.current = true;
                toast.success(t('checklistPieces.documentTeleverse', { libelle: piece.typePieceLibelle }));
            } catch (e) {
                toast.error(t('checklistPieces.erreurPiece', { libelle: piece.typePieceLibelle, message: e?.response?.data?.message || t('checklistPieces.echecTeleversement') }));
            } finally {
                setEnvois(({ [piece.trackingId]: _fini, ...reste }) => reste);
                envoisEnCours.current -= 1;
                load();
                // Le parent (compteurs, liste GED) ne se relit qu'une fois le lot terminé.
                if (envoisEnCours.current === 0 && unEnvoiReussi.current) {
                    unEnvoiReussi.current = false;
                    onDocumentTeleverse?.();
                }
            }
        };
        // `envoyer` ne rejette jamais : un échec ne bloque pas les envois suivants du même type.
        fileParTypeGed.current[typeDocument] = (fileParTypeGed.current[typeDocument] || Promise.resolve()).then(envoyer);
    };

    const rejeter = async (piece) => {
        try {
            await axiosInstance.patch(`/v1/dossiers/pieces/${piece.trackingId}/rejeter`, null, {
                params: { notes: 'Document non conforme' },
            });
            toast.success(t('checklistPieces.documentRejete'));
            load();
        } catch { toast.error(t('checklistPieces.erreur')); }
    };

    const retirer = async (piece) => {
        try {
            await axiosInstance.patch(`/v1/dossiers/pieces/${piece.trackingId}/retirer`);
            toast.success(t('checklistPieces.documentRetire'));
            load();
        } catch { toast.error(t('checklistPieces.erreur')); }
    };

    if (!maturite && !loading) return <Empty description={t('checklistPieces.aucunePiece')} />;

    const pct = maturite
        ? Math.round((maturite.nbPiecesRecues / Math.max(maturite.nbPiecesRequises, 1)) * 100)
        : 0;

    // Corporel : pièces complètes ne suffit pas, il faut la consolidation de la victime
    const attenteConsolidation = Boolean(
        maturite?.piecesCompletes && maturite?.consolidationRequise && !maturite?.estConsolidee
    );
    const enteteColor = maturite?.estMur ? '#166534' : attenteConsolidation ? '#7c3aed' : '#f97316';

    const TD_LABEL = {
        CORPOREL: t('checklistPieces.typesDommage.CORPOREL'),
        MATERIEL: t('checklistPieces.typesDommage.MATERIEL'),
        MIXTE: t('checklistPieces.typesDommage.MIXTE'),
        DECES: t('checklistPieces.typesDommage.DECES'),
        BLESSURE: t('checklistPieces.typesDommage.BLESSURE'),
    };
    const TD_COLOR = { CORPOREL: 'red', MATERIEL: 'orange', MIXTE: 'volcano', DECES: 'purple', BLESSURE: 'red' };

    // Une victime = un couloir (Blessure / Décès / Dommages matériels) : les pièces
    // arrivent avec leur `categorie`. Les anciennes lignes sans couloir gardent le
    // regroupement par typeDommage, le temps que les dossiers se resynchronisent.
    const communes = (maturite?.pieces || []).filter(p => !p.categorie && !p.typeDommage);
    const specifiquesParType = (maturite?.pieces || [])
        .filter(p => p.categorie || p.typeDommage)
        .reduce((acc, p) => {
            const key = p.categorie || p.typeDommage;
            if (!acc[key]) acc[key] = [];
            acc[key].push(p);
            return acc;
        }, {});

    return (
        <div>
            {/* ── Barre de maturité ── */}
            {maturite && (
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Space>
                            <FileDoneOutlined style={{ color: enteteColor, fontSize: 18 }} />
                            <Text strong style={{ color: enteteColor, fontSize: 14 }}>
                                {t('checklistPieces.dossier')} {maturite.estMur
                                    ? <><CheckCircleOutlined /> {t('checklistPieces.mur')}</>
                                    : attenteConsolidation
                                        ? <><ClockCircleOutlined /> {t('checklistPieces.attenteConsolidation')}</>
                                        : <><ClockCircleOutlined /> {t('checklistPieces.enCours')}</>}
                            </Text>
                        </Space>
                        <Space size={4}>
                            <Tag color="green">{t('checklistPieces.recues', { nombre: maturite.nbPiecesRecues })}</Tag>
                            <Tag color="orange">{t('checklistPieces.attendues', { nombre: maturite.nbPiecesAttendues })}</Tag>
                            {maturite.nbPiecesRejetees > 0 && <Tag color="red">{t('checklistPieces.rejetees', { nombre: maturite.nbPiecesRejetees })}</Tag>}
                            {maturite.consolidationRequise && (
                                <Tag color={maturite.estConsolidee ? 'green' : 'purple'}>
                                    {maturite.estConsolidee
                                        ? (maturite.dateConsolidation
                                            ? t('checklistPieces.consolideeLe', { date: new Date(maturite.dateConsolidation).toLocaleDateString(localeIntl()) })
                                            : t('checklistPieces.consolidee'))
                                        : t('checklistPieces.consolidationAttendue')}
                                </Tag>
                            )}
                            <Button size="small" icon={<ReloadOutlined />} onClick={load} style={{ borderRadius: 6 }} />
                        </Space>
                    </div>
                    <Progress
                        percent={pct}
                        status={maturite.estMur ? 'success' : 'active'}
                        strokeColor={maturite.estMur ? '#166534' : '#1a6b3c'}
                        format={() => t('checklistPieces.obligatoiresRecues', { recues: maturite.nbPiecesRecues, requises: maturite.nbPiecesRequises })}
                    />
                    {maturite.estMur && (
                        <Alert type="success" showIcon
                            message={t('checklistPieces.toutesRecues')}
                            style={{ marginTop: 12, borderRadius: 8 }} />
                    )}
                    {attenteConsolidation && (
                        <Alert type="info" showIcon
                            message={t('checklistPieces.attenteConsolidationMessage')}
                            style={{ marginTop: 12, borderRadius: 8 }} />
                    )}
                </div>
            )}

            {/* ── Pièces communes (tous types de dommage) ── */}
            {communes.length > 0 && (
                <PieceSection
                    titre={t('checklistPieces.piecesCommunes')}
                    pieces={communes}
                    onAssocier={ouvrirAssociation}
                    onTeleverser={can(HAB.GED_CREATE) ? televerser : null}
                    envois={envois}
                    onRejeter={rejeter}
                    onRetirer={retirer}
                />
            )}

            {/* ── Pièces spécifiques groupées par type de dommage ── */}
            {Object.entries(specifiquesParType).map(([type, pieces]) => (
                <PieceSection
                    key={type}
                    titre={
                        <span>
                            {t('checklistPieces.piecesAFournir')} <Tag color={TD_COLOR[type] || 'default'} style={{ borderRadius: 20 }}>
                                {TD_LABEL[type] || type}
                            </Tag>
                        </span>
                    }
                    pieces={pieces}
                    onAssocier={ouvrirAssociation}
                    onTeleverser={can(HAB.GED_CREATE) ? televerser : null}
                    envois={envois}
                    onRejeter={rejeter}
                    onRetirer={retirer}
                />
            ))}

            {/* ── Modal association doc GED ── */}
            <CBModal
                title={<><PaperClipOutlined /> {t('checklistPieces.associerDocumentGed', { libelle: modalPiece?.typePieceLibelle })}</>}
                open={!!modalPiece}
                onCancel={() => setModalPiece(null)}
                footer={null}
                width={560}
            >
                {docsGed.length === 0 ? (
                    <Alert type="warning" showIcon
                        message={t('checklistPieces.aucunDocumentGed')}
                        style={{ borderRadius: 8 }} />
                ) : (
                    <>
                        <div style={{ marginBottom: 12, color: '#6b7280', fontSize: 13 }}>
                            <Trans
                                t={t}
                                i18nKey="checklistPieces.selectionnezCorrespondant"
                                values={{ libelle: modalPiece?.typePieceLibelle }}
                                components={{ 1: <strong /> }}
                            />
                        </div>
                        <Select
                            style={{ width: '100%', marginBottom: 20 }}
                            placeholder={t('checklistPieces.choisirDocumentGed')}
                            value={docSelectionne}
                            onChange={setDocSelectionne}
                            showSearch
                            filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())}
                            options={docsGed.map(d => ({
                                value: d.ossanGedDocumentTrackingId,
                                label: `${d.titre} - ${TYPES_DOC_GED_MAP[d.typeDocument] || d.typeDocument} (${d.dateDocument || '-'})`,
                            }))}
                            getPopupContainer={t => t.parentElement}
                        />
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <Button onClick={() => setModalPiece(null)}>{t('checklistPieces.annuler')}</Button>
                            <Button type="primary" onClick={associer}
                                style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}>
                                {t('checklistPieces.associerCeDocument')}
                            </Button>
                        </div>
                    </>
                )}
            </CBModal>
        </div>
    );
}

// ── Sous-composant section ─────────────────────────────────────────
function PieceSection({ titre, pieces, onAssocier, onTeleverser, envois, onRejeter, onRetirer }) {
    const { t } = useTranslation('commun');
    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{
                fontSize: 12, fontWeight: 700, color: '#6b7280',
                textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8
            }}>
                {titre}
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {pieces.map((piece, i) => {
                    const cfg = STATUT_CONFIG[piece.statut] || STATUT_CONFIG.ATTENDUE;
                    const enEnvoi = !!envois?.[piece.trackingId];
                    const actions = [
                        (piece.statut === 'ATTENDUE' || piece.statut === 'REJETEE') && (
                            <Tooltip title={t('checklistPieces.associerUnDocumentGed')} key="assoc">
                                <Button size="small" icon={<LinkOutlined />}
                                    onClick={() => onAssocier(piece)}
                                    disabled={enEnvoi}
                                    style={{ borderRadius: 6, borderColor: '#1a6b3c', color: '#1a6b3c' }}>
                                    {t('checklistPieces.associer')}
                                </Button>
                            </Tooltip>
                        ),
                        (piece.statut === 'ATTENDUE' || piece.statut === 'REJETEE') && onTeleverser && (
                            <Tooltip title={t('checklistPieces.televerserAide')} key="upload">
                                <Upload
                                    accept="application/pdf"
                                    showUploadList={false}
                                    beforeUpload={(file) => { onTeleverser(piece, file); return false; }}
                                    disabled={enEnvoi}
                                >
                                    <Button size="small" icon={<PlusOutlined />}
                                        loading={enEnvoi}
                                        aria-label={t('checklistPieces.televerserPiece', { libelle: piece.typePieceLibelle })}
                                        style={{ borderRadius: 6, borderColor: '#1a6b3c', color: '#1a6b3c' }} />
                                </Upload>
                            </Tooltip>
                        ),
                        piece.statut === 'RECUE' && (
                            <Space key="actions-recue" size={4}>
                                <Tooltip title={t('checklistPieces.rejeterDocument')}>
                                    <Popconfirm title={t('checklistPieces.rejeterDocumentConfirm')} onConfirm={() => onRejeter(piece)} okText={t('checklistPieces.oui')} cancelText={t('checklistPieces.non')}>
                                        <Button size="small" icon={<StopOutlined />} danger style={{ borderRadius: 6 }} />
                                    </Popconfirm>
                                </Tooltip>
                                <Tooltip title={t('checklistPieces.retirerRepasser')}>
                                    <Popconfirm title={t('checklistPieces.retirerDocumentConfirm')} onConfirm={() => onRetirer(piece)} okText={t('checklistPieces.oui')} cancelText={t('checklistPieces.non')}>
                                        <Button size="small" icon={<DeleteOutlined />} style={{ borderRadius: 6 }} />
                                    </Popconfirm>
                                </Tooltip>
                            </Space>
                        ),
                    ].filter(Boolean);

                    return (
                        <li key={piece.typePieceTrackingId || i} style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            padding: '8px 12px', marginBottom: 6,
                            background: cfg.bg, borderRadius: 8,
                            borderLeft: `3px solid ${cfg.color}`,
                            border: `1px solid ${piece.obligatoire && piece.statut !== 'RECUE' ? cfg.color + '40' : '#e5e7eb'}`,
                        }}>
                            <span style={{ fontSize: 16, color: cfg.color, lineHeight: '20px' }}>{cfg.icon}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: piece.obligatoire ? 700 : 500, fontSize: 13 }}>
                                        {piece.typePieceLibelle}
                                    </span>
                                    {piece.obligatoire && (
                                        <Tag color="red" style={{ borderRadius: 20, fontSize: 10 }}>{t('checklistPieces.obligatoire')}</Tag>
                                    )}
                                    <Tag color={cfg.color === '#166534' ? 'green' : cfg.color === '#991b1b' ? 'red' : 'orange'}
                                        style={{ borderRadius: 20, fontSize: 10 }}>
                                        {cfg.label}
                                    </Tag>
                                </div>
                                {piece.statut === 'RECUE' && piece.ossanGedDocumentTitre ? (
                                    <span style={{ fontSize: 11, color: '#166534' }}>
                                        <PaperClipOutlined /> {piece.ossanGedDocumentTitre}
                                        {piece.dateReception && <> {t('checklistPieces.recuLe', { date: new Date(piece.dateReception).toLocaleDateString(localeIntl()) })}</>}
                                    </span>
                                ) : piece.statut === 'REJETEE' && piece.notes ? (
                                    <span style={{ fontSize: 11, color: '#991b1b' }}>
                                        <CloseCircleFilled /> {t('checklistPieces.rejeteNotes', { notes: piece.notes })}
                                    </span>
                                ) : piece.statut === 'ATTENDUE' && piece.typeDocumentGed ? (
                                    <span style={{ fontSize: 11, color: '#6b7280' }}>
                                        <ThunderboltOutlined /> <Trans
                                            t={t}
                                            i18nKey="checklistPieces.autoAssocie"
                                            values={{ type: TYPES_DOC_GED_MAP[piece.typeDocumentGed] || piece.typeDocumentGed }}
                                            components={{ 1: <strong /> }}
                                        />
                                    </span>
                                ) : null}
                            </div>
                            {actions.length > 0 && (
                                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>{actions}</div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
